import { makeStyles } from "@material-ui/core";
import React, { useState, useCallback, useEffect } from "react";
import { Select } from "antd";
import { REPORT_FONT, useReportFonts } from "../reportFonts";
import BackButton from "../BackButton";
import { goToReportsCatalog } from "../reportNav";
import { useHistory, useParams } from "react-router-dom";
import {
  FILTERS_CONFIG,
  initFilterValues,
  isFilterActive,
  getDisplayValue,
  getDefaultValue,
  getActiveFilterCount,
  areVisibleFilterValuesEqual,
  HorizontalDateRangeFilter,
  FilterApplyButton,
} from "./GlobalFilterPanel";
import { useExpandedAppliedFilters } from "./useExpandedAppliedFilters";
import {
  applyCascadeDraftChange,
  pruneInvalidCascadeSelections,
  getFilterFieldOptions,
  toFilterSelectOptions,
  FILTER_SELECT_SEARCH_PROPS,
  GlobalMultiSelectFilter,
} from "./filterRelationshipUtils";
import {
  usePlByProgramStyles,
  PlPageHeader,
  PL_PAGE_BG,
} from "./pl-by-program/plByProgramTheme";

import CurrentYearHeatmap from "./l2-tech-heatmap/CurrentYearHeatmap";
import YTDl2Heatmap from "./l2-tech-heatmap/YTDl2Heatmap";
import CurrentMonthL2Heatmap from "./l2-tech-heatmap/CurrentMonthL2Heatmap";
import Loading from "../../../common-components/Loading";
import {
  downloadL2HeatmapCurrentYearAPI,
  downloadL2HeatmapYTDAPI,
  downloadL2HeatmapCurrentMonthAPI,
} from "../../../api/reports-api";

import {
  TH_BG_BAND,
} from "../reportTableConfig";

const TAB_ACTIVE = "#4F46E5";
const BORDER = "#E5E7EB";

const MONTH_NAMES = [
  "Jan", "Feb", "Mar", "Apr", "May", "Jun",
  "Jul", "Aug", "Sep", "Oct", "Nov", "Dec",
];

const TABS = [
  { key: "current_year", label: "Current Year Heatmap" },
  { key: "ytd_l2", label: "YTD L2 Report" },
  { key: "current_month_l2", label: "Current Month L2 Report" },
];

/* ── Per-tab visible filter keys ─────────────────────────────── */
const TAB_FILTERS = {
  current_year: ["year", "programManager"],
  ytd_l2: ["year", "programManager"],
  current_month_l2: ["year", "programManager"],
};

/* Per-tab download endpoint + output filename */
const DOWNLOAD_BY_TAB = {
  current_year: { api: downloadL2HeatmapCurrentYearAPI, filename: "l2-current-year-heatmap.xlsx" },
  ytd_l2: { api: downloadL2HeatmapYTDAPI, filename: "l2-ytd-report.xlsx" },
  current_month_l2: { api: downloadL2HeatmapCurrentMonthAPI, filename: "l2-current-month-report.xlsx" },
};

function isOnlyDefaultYear(filterValues, vKeys) {
  const keys = vKeys
    ? vKeys.filter((k) => k !== "year")
    : FILTERS_CONFIG.filter((f) => f.key !== "year").map((f) => f.key);
  return (
    !keys.some((k) => {
      const f = FILTERS_CONFIG.find((fc) => fc.key === k);
      return f && isFilterActive(f, filterValues[k]);
    }) && String(filterValues.year) === String(new Date().getFullYear())
  );
}

const useStyles = makeStyles(() => ({
  page: {
    fontFamily: REPORT_FONT,
    backgroundColor: PL_PAGE_BG,
    display: "flex",
    flexDirection: "column",
    minHeight: "calc(100vh - 80px)",
  },
  topBar: {
    background: "#fff",
    padding: "12px 24px",
    display: "flex",
    alignItems: "center",
    gap: "12px",
    flexShrink: 0,
  },
  crumb: {
    display: "flex",
    alignItems: "center",
    gap: 8,
    fontSize: 14,
    color: "#9CA3AF",
    minWidth: 0,
  },
  crumbLink: {
    cursor: "pointer",
    color: "#9CA3AF",
    "&:hover": { color: "#6B7280" },
  },
  crumbSep: { color: "#D1D5DB", userSelect: "none" },
  crumbCur: { color: "#111827", fontWeight: 500 },
  topActions: {
    display: "flex",
    alignItems: "center",
    gap: 8,
    marginLeft: "auto",
    flexShrink: 0,
  },
  actionBtn: {
    display: "inline-flex",
    alignItems: "center",
    gap: 6,
    height: 32,
    padding: "0 12px",
    borderRadius: 6,
    border: "1px solid #E5E7EB",
    background: "#fff",
    color: "#374151",
    fontSize: 13,
    fontWeight: 500,
    cursor: "pointer",
    transition: "background 0.15s, border-color 0.15s",
    "&:hover": { background: "#F9FAFB", borderColor: "#D1D5DB" },
  },
  actionBtnActive: {
    borderColor: TAB_ACTIVE,
    background: TAB_ACTIVE,
    color: "#fff",
    "&:hover": { background: "#4338CA", borderColor: "#4338CA" },
  },
  actionBtnIconOnly: {
    width: 32,
    minWidth: 32,
    padding: 0,
    justifyContent: "center",
  },
  filterBadge: {
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    minWidth: 18,
    height: 18,
    padding: "0 5px",
    borderRadius: 9,
    background: "rgba(255,255,255,0.25)",
    color: "#fff",
    fontSize: 11,
    fontWeight: 700,
    lineHeight: 1,
  },
  tabBar: {
    background: "#fff",
    borderBottom: "1px solid #E5E7EB",
    display: "flex",
    flexWrap: "wrap",
    flexShrink: 0,
    padding: "0 24px",
    gap: "4px 0",
  },
  tab: {
    padding: "12px 16px",
    fontSize: 14,
    fontWeight: 500,
    color: "#374151",
    cursor: "pointer",
    whiteSpace: "nowrap",
    border: "none",
    borderBottom: "2px solid transparent",
    background: "none",
    marginBottom: -1,
    transition: "color 0.15s, border-color 0.15s",
    "&:hover": { color: TAB_ACTIVE },
  },
  tabActive: {
    color: TAB_ACTIVE,
    fontWeight: 700,
    borderBottom: `2px solid ${TAB_ACTIVE}`,
  },
  headerContainer: { margin: "20px 0 0", padding: "0 16px" },
  body: {
    flex: 1,
    display: "flex",
    flexDirection: "column",
    overflow: "hidden",
  },
  content: { flex: 1, padding: "0 16px", overflowY: "auto", minWidth: 0 },

  /* ── Horizontal filter bar ── */
  hFilterBar: {
    position: "relative",
    background: "#d6dbf8",
    borderTop: "2px solid #191b8c",
    borderBottom: `1px solid ${BORDER}`,
    padding: "12px 52px 12px 24px",
    display: "flex",
    alignItems: "flex-end",
    flexWrap: "wrap",
    gap: 10,
    flexShrink: 0,
  },
  hFilterGroup: {
    display: "flex",
    flexDirection: "column",
    gap: 4,
    flexShrink: 0,
    background: "#fff",
    borderRadius: 8,
    padding: "6px 10px 8px",
    minWidth: 120,
  },
  hFilterLabel: {
    fontSize: 10,
    fontWeight: 700,
    color: "#191b8c",
    textTransform: "uppercase",
    letterSpacing: "0.06em",
    whiteSpace: "nowrap",
  },
  hFilterInput: {
    height: 26,
    padding: "0 6px",
    border: "none",
    borderRadius: 4,
    fontSize: 12,
    fontFamily: REPORT_FONT,
    color: "#111827",
    outline: "none",
    minWidth: 110,
    background: "transparent",
    "&:focus": { outline: `1px solid ${TAB_ACTIVE}`, borderRadius: 4 },
  },
  hFilterActions: {
    display: "flex",
    alignItems: "flex-end",
    gap: 8,
    marginLeft: "auto",
    flexShrink: 0,
  },
  hFilterApply: {
    height: 34,
    padding: "0 16px",
    borderRadius: 6,
    border: "none",
    background: TAB_ACTIVE,
    color: "#fff",
    fontSize: 13,
    fontWeight: 600,
    cursor: "pointer",
    fontFamily: REPORT_FONT,
    whiteSpace: "nowrap",
    "&:hover": { background: "#4338CA" },
  "&:disabled": { opacity: 0.38, cursor: "not-allowed" },
  "&:disabled:hover": { background: "inherit" },
  },
  hFilterClearAll: {
    alignSelf: "flex-end",
    height: 34,
    padding: "0 12px",
    borderRadius: 6,
    border: `1px solid ${BORDER}`,
    background: "#fff",
    color: "#374151",
    fontSize: 13,
    fontWeight: 500,
    cursor: "pointer",
    flexShrink: 0,
    whiteSpace: "nowrap",
    fontFamily: REPORT_FONT,
    "&:hover": { color: TAB_ACTIVE, borderColor: TAB_ACTIVE },
    "&:disabled": { opacity: 0.38, cursor: "not-allowed" },
  },
  hFilterClose: {
    position: "absolute",
    top: 10,
    right: 10,
    width: 28,
    height: 28,
    borderRadius: "50%",
    border: "2px solid #191b8c",
    background: TH_BG_BAND,
    color: "#fff",
    fontSize: 13,
    fontWeight: 700,
    cursor: "pointer",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontFamily: REPORT_FONT,
    "&:hover": { background: "#111827" },
  },

  /* ── Active filter chips ── */
  chipsBar: {
    display: "flex",
    alignItems: "center",
    gap: 6,
    padding: "8px 16px",
    background: "#F8F9FF",
    borderBottom: `1px solid ${BORDER}`,
    flexWrap: "wrap",
    flexShrink: 0,
  },
  chip: {
    display: "inline-flex",
    alignItems: "center",
    gap: 5,
    height: 26,
    padding: "0 10px",
    borderRadius: 20,
    background: TH_BG_BAND,
    border: `1px solid ${TH_BG_BAND}`,
    fontSize: 11,
    fontWeight: 500,
    color: "#F1F5F9",
    whiteSpace: "nowrap",
    flexShrink: 0,
  },
  chipLabel: { color: "#94A3B8", fontWeight: 400, marginRight: 2 },
  chipClear: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    width: 14,
    height: 14,
    borderRadius: "50%",
    background: "none",
    border: "none",
    cursor: "pointer",
    color: "#94A3B8",
    fontSize: 10,
    padding: 0,
    marginLeft: 2,
    "&:hover": { background: "rgba(255,255,255,0.15)", color: "#fff" },
    "&:disabled": { opacity: 0.38, cursor: "not-allowed" },
  },
  chipsClearAll: {
    fontSize: 11,
    fontWeight: 600,
    color: "#6B7280",
    background: "none",
    border: "none",
    cursor: "pointer",
    padding: "0 6px",
    marginLeft: 4,
    whiteSpace: "nowrap",
    flexShrink: 0,
    "&:hover": { color: TAB_ACTIVE },
    "&:disabled": { opacity: 0.38, cursor: "not-allowed" },
  },
}));

function FilterIcon() {
  return (
    <svg
      width="14"
      height="14"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <line x1="4" y1="6" x2="20" y2="6" />
      <line x1="8" y1="12" x2="16" y2="12" />
      <line x1="10" y1="18" x2="14" y2="18" />
    </svg>
  );
}

function DownloadIcon() {
  return (
    <svg
      width="14"
      height="14"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
      <polyline points="7 10 12 15 17 10" />
      <line x1="12" y1="15" x2="12" y2="3" />
    </svg>
  );
}

/* ── Horizontal filter bar component ─────────────────────────── */
function HorizontalFilterBar({
  visibleKeys,
  draft,
  dropdownOptions,
  onChange,
  onClearAll,
  onApply,
  onClose,
  classes,
  clearAllDisabled,
  applyDisabled,
}) {
  const filters = visibleKeys
    ? FILTERS_CONFIG.filter((f) => visibleKeys.includes(f.key))
    : FILTERS_CONFIG;

  const selectStyle = { minWidth: 110, maxWidth: 180, fontSize: 12 };

  return (
    <div className={classes.hFilterBar}>
      {filters.map((f) => {
        const opts = toFilterSelectOptions(
          f.key,
          getFilterFieldOptions(f, dropdownOptions),
        );
        const val = draft[f.key];

        return (
          <div key={f.key} className={classes.hFilterGroup}>
            <span className={classes.hFilterLabel}>{f.label}</span>

            {f.type === "select" && (
              <Select
                size="small"
                style={selectStyle}
                value={val || (f.key === "year" ? opts[0]?.value : "All")}
                onChange={(v) =>
                  onChange(f.key, v === "All" ? f.defaultValue : v)
                }
                options={
                  f.key === "year"
                    ? opts
                    : [{ value: "All", label: "All" }, ...opts]
                }
                {...FILTER_SELECT_SEARCH_PROPS}
                popupMatchSelectWidth={false}
              />
            )}

            {f.type === "multiselect" && (
              <GlobalMultiSelectFilter
                size="small"
                style={selectStyle}
                value={val}
                options={opts}
                onChange={(v) => onChange(f.key, v)}
                {...FILTER_SELECT_SEARCH_PROPS}
                popupMatchSelectWidth={false}
              />
            )}

            {f.type === "text" && (
              <input
                className={classes.hFilterInput}
                value={val || ""}
                placeholder="All"
                onChange={(e) => onChange(f.key, e.target.value)}
              />
            )}
            {f.type === "daterange" && (
              <HorizontalDateRangeFilter
                value={val}
                onChange={(next) => onChange(f.key, next)}
              />
            )}
          </div>
        );
      })}

      <div className={classes.hFilterActions}>
        <button
          className={classes.hFilterClearAll}
          onClick={onClearAll}
          disabled={clearAllDisabled}
        >
          Clear All
        </button>
        <FilterApplyButton
          draft={draft}
          onApply={onApply}
          className={classes.hFilterApply}
          disabled={applyDisabled}
        >
          Apply
        </FilterApplyButton>
      </div>

      <button
        className={classes.hFilterClose}
        onClick={onClose}
        title="Close Filters"
      >
        ✕
      </button>
    </div>
  );
}

export default function L2TechHeatmap() {
  const classes = useStyles();
  const plClasses = usePlByProgramStyles();
  useReportFonts();
  const history = useHistory();

  const { tab: tabParam } = useParams();
  const activeTab = TABS.find((t) => t.key === tabParam)?.key ?? TABS[0].key;
  const [filterOpen, setFilterOpen] = useState(true);
  const [appliedFilters, setAppliedFilters] = useState(initFilterValues);
  const [draftFilters, setDraftFilters] = useState(initFilterValues);
  const [cyMeta, setCyMeta] = useState(null);
  const [ytdMeta, setYtdMeta] = useState(null);
  const [cmMeta, setCmMeta] = useState(null);
  const [downloading, setDownloading] = useState(false);

  const visibleKeys = TAB_FILTERS[activeTab] || null;
  const { dropdownOptions, relationshipRows, effectiveFilters } = useExpandedAppliedFilters(
    appliedFilters,
    draftFilters,
    visibleKeys,
  );

  useEffect(() => {
    if (filterOpen) {
      setDraftFilters(
        pruneInvalidCascadeSelections(
          appliedFilters,
          relationshipRows,
          visibleKeys,
        ),
      );
    }
  }, [filterOpen, appliedFilters, relationshipRows, visibleKeys]);

  const filterCount = getActiveFilterCount(appliedFilters, visibleKeys);

  const activeChips = (
    visibleKeys
      ? FILTERS_CONFIG.filter((f) => visibleKeys.includes(f.key))
      : FILTERS_CONFIG
  ).filter((f) => isFilterActive(f, appliedFilters[f.key]));

  const clearChip = useCallback((f) => {
    setAppliedFilters((prev) => ({ ...prev, [f.key]: getDefaultValue(f) }));
    setDraftFilters((prev) => ({ ...prev, [f.key]: getDefaultValue(f) }));
  }, []);

  const clearAllApplied = useCallback(() => {
    const reset = (prev) => {
      const next = { ...prev };
      (visibleKeys || FILTERS_CONFIG.map((f) => f.key)).forEach((k) => {
        const f = FILTERS_CONFIG.find((fc) => fc.key === k);
        if (f) next[k] = getDefaultValue(f);
      });
      return next;
    };
    setAppliedFilters(reset);
    setDraftFilters(reset);
  }, [visibleKeys]);

  const handleDraftChange = useCallback(
    (key, value) => {
      setDraftFilters((prev) =>
        applyCascadeDraftChange(
          prev,
          key,
          value,
          relationshipRows,
          visibleKeys,
        ),
      );
    },
    [relationshipRows, visibleKeys],
  );

  const handleApply = useCallback(() => {
    setAppliedFilters(
      pruneInvalidCascadeSelections(
        draftFilters,
        relationshipRows,
        visibleKeys,
      ),
    );
  }, [draftFilters, relationshipRows, visibleKeys]);

  const clearAll = useCallback(() => {
    setDraftFilters((prev) => {
      const next = { ...prev };
      (visibleKeys || FILTERS_CONFIG.map((f) => f.key)).forEach((k) => {
        const f = FILTERS_CONFIG.find((fc) => fc.key === k);
        if (f) next[k] = getDefaultValue(f);
      });
      return next;
    });
  }, [visibleKeys]);

  const actualsThroughLabel = (meta) => {
    const m = Number(meta?.actualThroughMonth);
    const year = meta?.fiscalYear ?? new Date().getFullYear();
    if (!m || m < 1 || m > 12) return `Actuals through May ${year}`;
    return `Actuals through ${MONTH_NAMES[m - 1]} ${year}`;
  };

  const TAB_HEADER = {
    current_year: {
      eyebrow: "L2 Tech II",
      title: "Current Year Heatmap",
      accent: "FY-2026 Full Year",
      badge: "Live Forecast",
      date: actualsThroughLabel(cyMeta),
    },
    ytd_l2: {
      eyebrow: "L2 Tech II",
      title: "YTD L2 Report",
      accent: "Year to Date",
      badge: "Live Forecast",
      date: actualsThroughLabel(ytdMeta),
    },
    current_month_l2: {
      eyebrow: "L2 Tech II",
      title: "Current Month L2 Report",
      accent: "Monthly BUFRs",
      badge: "Live Forecast",
      date: actualsThroughLabel(cmMeta),
    },
  };

  const handleDownload = useCallback(async () => {
    const entry = DOWNLOAD_BY_TAB[activeTab];
    if (!entry || downloading) return;
    setDownloading(true);
    try {
      const monthName =
        activeTab === "current_month_l2" && cmMeta?.actualThroughMonth
          ? MONTH_NAMES[Number(cmMeta.actualThroughMonth) - 1]
          : undefined;
      const { blob, filename } = await entry.api(effectiveFilters, monthName);
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = filename || entry.filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (err) {
      console.error("L2 Tech Heatmap download failed:", err);
    } finally {
      setDownloading(false);
    }
  }, [activeTab, effectiveFilters, downloading, cmMeta]);

  return (
    <div className={classes.page}>
      {downloading && <Loading />}
      {/* ── Top bar ── */}
      <div className={classes.topBar}>
        <BackButton
          onClick={() => goToReportsCatalog(history)}
          ariaLabel="Back to Reports"
        />
        <nav className={classes.crumb}>
          <span
            className={classes.crumbLink}
            onClick={() => goToReportsCatalog(history)}
          >
            Reports
          </span>
          <span className={classes.crumbSep}>&gt;</span>
          <span className={classes.crumbCur}>L2 Tech II Heatmap</span>
        </nav>
        <div className={classes.topActions}>
          <button
            type="button"
            className={`${classes.actionBtn} ${classes.actionBtnIconOnly} ${classes.actionBtnActive}`}
            title="Download as Excel"
            aria-label="Download Report"
            onClick={handleDownload}
            disabled={downloading}
          >
            <DownloadIcon />
          </button>
          <button
            type="button"
            className={`${classes.actionBtn} ${filterOpen || filterCount > 0 ? classes.actionBtnActive : ""}`}
            onClick={() => setFilterOpen((p) => !p)}
          >
            <FilterIcon />
            Filters
            {filterCount > 0 && (
              <span className={classes.filterBadge}>{filterCount}</span>
            )}
          </button>
        </div>
      </div>

      {/* ── Tab bar ── */}
      <div className={classes.tabBar}>
        {TABS.map((tab) => (
          <button
            key={tab.key}
            type="button"
            className={`${classes.tab} ${activeTab === tab.key ? classes.tabActive : ""}`}
            onClick={() =>
              history.push(`/projects/reports/l2_tech_heatmap/${tab.key}`)
            }
          >
            {tab.label}
          </button>
        ))}
      </div>

      {/* ── Horizontal filter bar ── */}
      {filterOpen && (
        <HorizontalFilterBar
          visibleKeys={visibleKeys}
          draft={draftFilters}
          dropdownOptions={dropdownOptions}
          onChange={handleDraftChange}
          onClearAll={clearAll}
          onApply={handleApply}
          applyDisabled={areVisibleFilterValuesEqual(draftFilters, appliedFilters, visibleKeys)}
          onClose={() => setFilterOpen(false)}
          classes={classes}
          clearAllDisabled={isOnlyDefaultYear(draftFilters, visibleKeys)}
        />
      )}

      {/* ── Active filter chips (shown when filter bar is hidden) ── */}
      {!filterOpen && activeChips.length > 0 && (
        <div className={classes.chipsBar}>
          {activeChips.map((f) => {
            const val = appliedFilters[f.key];
            const tip =
              f.type === "multiselect" && Array.isArray(val) && val.length > 1
                ? `${f.label}: ${val.join(", ")}`
                : `${f.label}: ${getDisplayValue(f, val)}`;
            return (
              <span key={f.key} className={classes.chip} title={tip}>
                <span className={classes.chipLabel}>{f.label}:</span>
                {getDisplayValue(f, val)}
                <button
                  className={classes.chipClear}
                  onClick={() => clearChip(f)}
                  title={`Clear ${f.label}`}
                  disabled={
                    f.key === "year" &&
                    String(val) === String(new Date().getFullYear())
                  }
                >
                  ✕
                </button>
              </span>
            );
          })}
          <button
            className={classes.chipsClearAll}
            onClick={clearAllApplied}
            disabled={isOnlyDefaultYear(appliedFilters, visibleKeys)}
          >
            Clear All
          </button>
        </div>
      )}

      {/* ── Page header ── */}
      <div className={classes.headerContainer}>
        <PlPageHeader
          classes={plClasses}
          eyebrow={TAB_HEADER[activeTab].eyebrow}
          title={TAB_HEADER[activeTab].title}
          accent={TAB_HEADER[activeTab].accent}
          badge={TAB_HEADER[activeTab].badge}
          date={TAB_HEADER[activeTab].date}
        />
      </div>

      {/* ── Content ── */}
      <div className={classes.body}>
        <div className={classes.content}>
          {activeTab === "current_year" && (
            <CurrentYearHeatmap filters={effectiveFilters} onMeta={setCyMeta} />
          )}
          {activeTab === "ytd_l2" && (
            <YTDl2Heatmap filters={effectiveFilters} onMeta={setYtdMeta} />
          )}
          {activeTab === "current_month_l2" && (
            <CurrentMonthL2Heatmap filters={effectiveFilters} onMeta={setCmMeta} />
          )}
        </div>
      </div>
    </div>
  );
}
